import { Injectable } from '@angular/core';
import { IDraggable, IDropTarget } from '@features/drag-and-drop/drag-and-drop.types';
import { BiMultiMap } from '@shared/bimap';
import { subtract } from '@shared/vector.utils';
import { Vector2Screen } from '@typings/vectors';

@Injectable({
  providedIn: 'root',
})
export class DragAndDropService {
  private targets: IDropTarget[] = [];
  private draggables: IDraggable[] = [];
  private lastZIndex = 0;
  private entered: BiMultiMap<IDraggable, IDropTarget>;
  private dropped: BiMultiMap<IDraggable, IDropTarget>;

  constructor() {
    this.entered = new BiMultiMap<IDraggable, IDropTarget>();
    this.dropped = new BiMultiMap<IDraggable, IDropTarget>();
  }

  public registerDraggable(draggable: IDraggable) {
    this.draggables.push(draggable);
  }

  public unregisterDraggable(draggable: IDraggable) {
    const index = this.draggables.indexOf(draggable);
    if (index !== -1) {
      this.draggables.splice(index, 1);
    }
    this.freeDraggable(draggable, true);
  }

  public registerDropTarget(target: IDropTarget) {
    this.targets.push(target);
  }

  public unregisterDropTarget(target: IDropTarget) {
    const index = this.targets.indexOf(target);
    if (index !== -1) {
      this.targets.splice(index, 1);
    }

    this.freeTarget(target, true);
  }

  public grab(draggable: IDraggable, position: Vector2Screen) {
    this.drag(draggable, position);
    draggable.setZIndex(++this.lastZIndex);
  }

  public drag(draggable: IDraggable, position: Vector2Screen) {
    const target = this.getTargetForDraggable(draggable, position);
    if (target) {
      this.translateToTarget(draggable, target);
      if (!this.entered.has(draggable, target)) {
        this.entered.add(draggable, target);
        draggable.entered(target);
      }
    } else {
      this.translateTo(draggable, position);
      for (const target of this.entered.getBs(draggable)) {
        this.entered.remove(draggable, target);
        draggable.exited(target);
      }
    }
  }

  public release(draggable: IDraggable, position: Vector2Screen) {
    this.freeDraggable(draggable);

    const target = this.getTargetForDraggable(draggable, position);
    if (target) {
      this.clearTarget(target);

      this.translateToTarget(draggable, target);
      this.insertIntoTarget(draggable, target);
    } else {
      this.translateToOrigin(draggable);
    }
  }

  public clearDraggable(draggable: IDraggable): void {
    this.freeDraggable(draggable);
    this.translateToOrigin(draggable);
  }

  public translateToTarget(draggable: IDraggable, target: IDropTarget): void {
    this.translateTo(draggable, target.getPivot());
  }

  public translateTo(draggable: IDraggable, position: Vector2Screen): void {
    const pivot = draggable.getPivotWithoutTransform();
    const translation = subtract(position, pivot) as Vector2Screen;
    draggable.setTranslation(translation);
  }

  private freeDraggable(draggable: IDraggable, silent?: boolean) {
    const targets = this.dropped.getBs(draggable);
    for (const target of targets) {
      if (!silent) {
        target.removed(draggable);
        draggable.setTarget(undefined); // TODO: might still be in other targets
      }
      this.dropped.remove(draggable, target);
    }
  }

  private freeTarget(target: IDropTarget, silent?: boolean) {
    const draggables = this.dropped.getAs(target);
    for (const draggable of draggables) {
      if (!silent) {
        target.removed(draggable);
        draggable.setTarget(undefined); // TODO: might still be in other targets
      }
      this.dropped.remove(draggable, target);
    }
  }

  private insertIntoTarget(draggable: IDraggable, target: IDropTarget) {
    this.dropped.add(draggable, target);
    target.added(draggable);
    draggable.setTarget(target);
  }

  private getTargetForDraggable(draggable: IDraggable, cursorPosition: Vector2Screen): IDropTarget | undefined {
    const relevantTargets = this.targets.filter((target) => {
      const rect = target.getRect();
      const isInRect =
        cursorPosition[0] >= rect.pos[0] &&
        cursorPosition[0] <= rect.pos[0] + rect.size[0] &&
        cursorPosition[1] >= rect.pos[1] &&
        cursorPosition[1] <= rect.pos[1] + rect.size[1];
      return isInRect;
    });
    if (relevantTargets.length === 0) return undefined;

    return relevantTargets[0];
  }

  private clearTarget(target: IDropTarget): void {
    const oldDraggables = this.dropped.getAs(target);
    for (const oldDraggable of oldDraggables) {
      this.freeDraggable(oldDraggable);
      this.translateToOrigin(oldDraggable);
    }
  }

  private translateToOrigin(draggable: IDraggable): void {
    draggable.setTarget(undefined);
    draggable.setTranslation([0, 0] as Vector2Screen);
    for (const target of this.entered.getBs(draggable)) {
      this.entered.remove(draggable, target);
      draggable.exited(target);
    }
  }
}
